import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import '../Login/Login.scss';
import './SetPassword.scss';
import { useProfile } from '../../Contexts/ProfileContext';
import { noAutofillProps } from '../noAutofillProps';

export function SetPassword() {
  const navigate = useNavigate();
  const { fetchProfile, updateProfileData } = useProfile();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setResponseMessage('');
  };

  const handleSetPassword = async (event) => {
    event.preventDefault();

    const MIN_PASSWORD_LENGTH = 6;
    if (!formData.password || formData.password.length < MIN_PASSWORD_LENGTH) {
      setResponseMessage(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setResponseMessage('Passwords do not match.');
      return;
    }

    setLoading(true);
    setResponseMessage('');

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.put(
        '/users/set-password',
        { password: formData.password, confirmPassword: formData.confirmPassword },
        config
      );

      if (response?.status === 200) {
        localStorage.setItem('passwordSet', 'true');
        updateProfileData({ data: { ...(response?.data?.data || {}), passwordSet: true } });
        await fetchProfile(true);
        navigate('/');
      } else {
        setResponseMessage(response?.data?.msg || 'Could not set password.');
      }
    } catch (error) {
      const msg = error.response?.data?.msg || error.response?.data?.message || 'Could not set password.';
      setResponseMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/');
  };

  return (
    <div className={`web-authPage${loading ? ' auth-busy' : ''}`} aria-busy={loading}>
      <div className="auth-container">
        <div className="LazyLoad is-visible" style={{ width: '100%', background: 'rgb(255, 242, 223)' }}>
          <picture className="img-responsive" style={{ width: '100%' }}>
            <source
              srcSet="//assets.myntassets.com/f_webp,dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2023/10/29/9610da61-a1a4-4215-b1fa-f612242d10e61698602536819-Flat_200--1-.jpg"
              type="image/webp"
            />
            <img
              src="//assets.myntassets.com/dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2023/10/29/9610da61-a1a4-4215-b1fa-f612242d10e61698602536819-Flat_200--1-.jpg"
              className="img-responsive preLoad loaded"
              alt=""
              title=""
              style={{ width: '100%' }}
            />
          </picture>
        </div>
        <div className="signInContainer">
          <div className="welcome-header">
            Set your password
            <span className="welcome-header-small">Create a password to sign in faster next time.</span>
          </div>
          <div className="mobileInputContainer">
            <form onSubmit={handleSetPassword} autoComplete="off">
              <div className="form-group">
                <input
                  type="password"
                  name="password"
                  className="form-control mobileNumberInput"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                  minLength={6}
                  {...noAutofillProps({ autoComplete: 'new-password' })}
                />
                <span className="placeholderAlternative mobileNumber">
                  Password <span className="field-optional-label">(min 6 characters)</span>
                </span>
                <i className="bar" />
              </div>

              <div className="form-group">
                <input
                  type="password"
                  name="confirmPassword"
                  className="form-control mobileNumberInput"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  minLength={6}
                  {...noAutofillProps({ autoComplete: 'new-password' })}
                />
                <span className="placeholderAlternative mobileNumber">Confirm Password</span>
                <i className="bar" />
              </div>

              {responseMessage && (
                <div className="responseMessage">
                  <i className="bi bi-exclamation-circle-fill" />
                  <span>{responseMessage}</span>
                </div>
              )}

              <div className="midLinks">
                By continuing, I agree to the{' '}
                <a href="/terms">Terms of Use</a> &amp;{' '}
                <a href="/privacy">Privacy Policy</a>
              </div>

              <button type="submit" className="submitBottomOption set-password-submit" disabled={loading}>
                {loading ? 'Setting...' : 'SET PASSWORD'}
              </button>
            </form>
          </div>

          <div className="get-help">
            Skip for now?{' '}
            <span
              className={`get-help-action${loading ? ' get-help-action--disabled' : ''}`}
              onClick={loading ? undefined : handleSkip}
              onKeyDown={(e) => {
                if (loading) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSkip();
                }
              }}
              role="button"
              tabIndex={loading ? -1 : 0}
              aria-disabled={loading}
            >
              Set Later
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
